import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import { useRole } from "../../components/AppShell";
import { Avatar, Empty, ListSkeleton } from "../../components/ui";
import { formatPhone, shortTime } from "../../lib/format";
import { isWaiting, useNow, WAIT_LATE_MIN, waitLabel, waitLevel, waitMinutes } from "../../lib/inbox";
import { type InboxFilter, useConversations, useTeam, useWaitingConversations } from "../../lib/queries";
import type { ConversationSummary } from "../../lib/types";
import { ConversationView } from "./ConversationView";

const SCOPES: { id: InboxFilter; label: string }[] = [
  { id: "all", label: "Todas" },
  { id: "mine", label: "Mías" },
];

export interface InboxSearch {
  c?: string;
  f?: InboxFilter;
}

/**
 * The inbox puts whoever is waiting for a person first, longest wait on top; then what
 * the IA is handling, then conversations the team is following up.
 */
export function InboxPage() {
  const search = useSearch({ from: "/app/bandeja" });
  const navigate = useNavigate({ from: "/bandeja" });
  const filter = search.f ?? "all";
  const list = useConversations(filter);
  const waitingList = useWaitingConversations(filter);
  const team = useTeam();
  const now = useNow();
  // Owners and admins see who is waiting with nobody in charge (Lumi could not tell the area).
  const role = useRole();
  const manager = role === "owner" || role === "admin";
  const unassigned = useWaitingConversations("unassigned", manager).data?.items.length ?? 0;
  const selected = search.c;
  const names = useMemo(() => new Map(team.data?.items.map((m) => [m.id, m.name])), [team.data]);

  const paged = list.data?.pages.flatMap((p) => p.items) ?? [];
  const extra = (waitingList.data?.items ?? []).filter((w) => !paged.some((c) => c.id === w.id));
  const items = [...paged, ...extra];
  const waiting = items
    .filter(isWaiting)
    .sort((a, b) => Date.parse(a.lastInboundAt ?? "") - Date.parse(b.lastInboundAt ?? ""));
  const groups: [string, ConversationSummary[]][] =
    filter === "closed"
      ? [["Cerradas", items]]
      : [
          ["Esperan respuesta", waiting],
          ["Con la IA", items.filter((c) => c.status === "bot")],
          ["En seguimiento", items.filter((c) => c.status === "human" && !isWaiting(c))],
        ];
  const late = waiting.filter((c) => waitMinutes(c.lastInboundAt, now) >= WAIT_LATE_MIN).length;
  const open = (c: string) => navigate({ search: { f: filter, c } });

  return (
    <div className="inbox">
      <section className="inbox-list" aria-label="Conversaciones">
        <header>
          <div className="inbox-title">
            <h1>Bandeja</h1>
            {filter !== "closed" && list.data && (
              <span className={`inbox-summary${late ? " late" : ""}`}>
                {late
                  ? `${late} esperando más de ${WAIT_LATE_MIN} min`
                  : waiting.length === 1
                    ? "1 espera respuesta"
                    : waiting.length
                      ? `${waiting.length} esperan respuesta`
                      : "Todo al día"}
              </span>
            )}
          </div>
          <fieldset className="filters">
            <legend className="sr-only">Mostrar</legend>
            {SCOPES.map((s) => (
              <button
                key={s.id}
                type="button"
                aria-pressed={filter === s.id}
                onClick={() => navigate({ search: { f: s.id, c: selected } })}
              >
                {s.label}
              </button>
            ))}
            {manager && (
              <button
                type="button"
                aria-pressed={filter === "unassigned"}
                className={unassigned ? "needs-assignee" : undefined}
                onClick={() => navigate({ search: { f: "unassigned", c: selected } })}
              >
                Sin responsable{unassigned ? <span className="filter-count">{unassigned}</span> : null}
              </button>
            )}
          </fieldset>
        </header>
        <div className="conv-items">
          {list.isPending && <ListSkeleton />}
          {list.isError && <Empty title="No pudimos cargar la bandeja.">{list.error.message}</Empty>}
          {list.data && items.length === 0 && (
            <Empty
              title={
                filter === "closed"
                  ? "No hay conversaciones cerradas."
                  : filter === "mine"
                    ? "No tienes conversaciones asignadas."
                    : "No hay conversaciones abiertas."
              }
            >
              <span>
                {filter === "mine"
                  ? "Toma una desde Todas o pide que te asignen una."
                  : "Cuando un cliente escriba al WhatsApp de tu empresa, aparecerá aquí."}
              </span>
            </Empty>
          )}
          {groups
            .filter(([, xs]) => xs.length > 0)
            .map(([name, xs]) => (
              <div key={name} className="conv-group">
                <h2>
                  <span>{name}</span>
                  <span>{xs.length}</span>
                </h2>
                {xs.map((c) => (
                  <ConversationRow
                    key={c.id}
                    c={c}
                    now={now}
                    selected={selected === c.id}
                    assignee={c.assigneeId ? names.get(c.assigneeId) : undefined}
                    onOpen={() => open(c.id)}
                  />
                ))}
              </div>
            ))}
          {list.hasNextPage && (
            <button
              type="button"
              className="btn btn-sm conv-more"
              disabled={list.isFetchingNextPage}
              onClick={() => list.fetchNextPage()}
            >
              {list.isFetchingNextPage ? "Cargando…" : "Ver más conversaciones"}
            </button>
          )}
          {list.data && (
            <button
              type="button"
              className="btn btn-ghost btn-sm conv-closed-link"
              onClick={() => navigate({ search: { f: filter === "closed" ? "all" : "closed", c: selected } })}
            >
              {filter === "closed" ? "Volver a las abiertas" : "Ver cerradas"}
            </button>
          )}
        </div>
      </section>

      {selected ? (
        <ConversationView key={selected} conversationId={selected} />
      ) : (
        <div className="chat-empty">
          <div>
            <strong>Elige una conversación</strong>
            <p className="muted">Las que esperan respuesta van primero.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ConversationRow({
  c,
  now,
  selected,
  assignee,
  onOpen,
}: {
  c: ConversationSummary;
  now: number;
  selected: boolean;
  assignee: string | undefined;
  onOpen: () => void;
}) {
  const waiting = isWaiting(c);
  const minutes = waiting ? waitMinutes(c.lastInboundAt, now) : 0;
  const level = waitLevel(minutes);
  const stripe = c.status === "bot" ? "s-ia" : waiting ? (level === "late" ? "s-late" : "s-wait") : "";
  const who = c.status === "bot" ? "IA" : c.assigneeId ? (assignee ?? "Asignada") : "Sin asignar";
  return (
    <button type="button" className={`conv-item ${stripe}`} aria-current={selected} onClick={onOpen}>
      {/* The ring around the initials carries the status: waiting, waiting long, with the IA. */}
      <Avatar name={c.contact.name} fallback={c.contact.waId} />
      <span className="name" title={c.contact.name ?? undefined}>
        {c.contact.name ?? formatPhone(c.contact.waId)}
      </span>
      {waiting ? (
        <span className={`wait ${level}`} title="Tiempo esperando respuesta">
          {waitLabel(minutes)}
        </span>
      ) : (
        <span className="time">{shortTime(c.lastMessageAt)}</span>
      )}
      <span className="preview" title={c.lastMessagePreview ?? undefined}>
        {c.lastMessagePreview}
      </span>
      <span className="meta">
        {c.contact.tags?.[0] && (
          <span className="topic" title={c.contact.tags.join(", ")}>
            {c.contact.tags[0]}
          </span>
        )}
        <span>{who}</span>
      </span>
    </button>
  );
}
